import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useState } from "react";
import Skeleton from "react-loading-skeleton";

import { AppConfig } from "~/AppConfig";
import { isWhiteLabel } from "~/utils/white-label";

import { AccessToken } from "../../api/Api";
import { Icon } from "../../assets/icons/Icon";
import {
  Pagination,
  PaginationConfig,
} from "../../components/ui/pagination/Pagination";
import { formatTableDate } from "../../helpers/helpers";
import NoData from "../containers/NoData";
import Button from "../ui/button/Button";
import MaskedText from "../ui/maskedText/MaskedText";
import Modal from "../ui/modal/Modal";
import { SearchBar } from "../ui/search/SearchBar";
import styles from "./ApiKeysTable.module.scss";
import Popover from "./Popover";

const MESSAGE = "API Key successfully copied to clipboard!";
const API_DOCS_URL = `${AppConfig.API_URL}/docs`;

const renderWithSkeleton = (isLoading: boolean, content: React.ReactNode) =>
  isLoading ? <Skeleton /> : content;

type ApiKeysTableProps = {
  data: AccessToken[];
  isLoading: boolean;
  pagination: PaginationConfig;
  setPagination: (pagination: PaginationConfig) => void;
  onDelete: (id: number) => void;
  onCreate: () => void;
  isPendingApiKeyDelete: boolean;
  isPendingApiKeyCreate: boolean;
  isFetched: boolean;
};

const ApiKeysTable: React.FC<ApiKeysTableProps> = ({
  data,
  isLoading,
  pagination,
  setPagination,
  onDelete,
  isPendingApiKeyCreate,
  onCreate,
  isFetched,
}) => {
  const [popoverOpen, setPopoverOpen] = useState<number | null>(null);
  const [openModal, setOpen] = useState(false);
  const handleOpenPopover = (id: number) => setPopoverOpen(id);
  const handleClosePopover = () => setPopoverOpen(null);
  const [currentId, setCurrentId] = useState<number | null>(null);

  const handleDelete = () => {
    if (currentId) {
      onDelete(currentId);
    }
    handleClosePopover();
    setOpen(false);
    setCurrentId(null);
  };

  const handleClose = () => {
    setOpen(false);
  };
  const handleOpen = () => {
    setOpen(true);
  };
  const columnHelper = createColumnHelper<AccessToken>();

  const columns = [
    columnHelper.accessor("key", {
      header: "API Key",
      cell: (info) =>
        renderWithSkeleton(
          isLoading,
          <MaskedText text={info.getValue()} message={MESSAGE} />,
        ),
    }),
    columnHelper.accessor("created_at", {
      header: "Created",
      cell: (info) =>
        renderWithSkeleton(isLoading, formatTableDate(info?.getValue() || "")),
    }),
    columnHelper.accessor("updated_at", {
      header: "Last Used",
      cell: (info) => {
        const { never_used, updated_at } = info.row.original;

        return renderWithSkeleton(
          isLoading,
          never_used ? "never used" : formatTableDate(updated_at || ""),
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      cell: (info) => {
        const id = info.row.original.id;

        return (
          <div style={{ position: "relative" }}>
            {renderWithSkeleton(
              isLoading,
              <Button
                noHover
                // @ts-expect-error TS2551 non-existing className
                className={styles.apiKeysTableIconButton}
                onClick={() => {
                  setCurrentId(id);
                  handleOpenPopover(id);
                }}
              >
                <Icon
                  iconName="TreeDotsButtonIcon"
                  width="1rem"
                  height="1rem"
                />
              </Button>,
            )}
            <Popover
              isOpen={popoverOpen === id}
              onClose={handleClosePopover}
              onDelete={handleOpen}
            />
          </div>
        );
      },
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handlePageChange = (page: number) =>
    setPagination({ ...pagination, currentPage: page });

  const handleItemsPerPageChange = (items: number) =>
    setPagination({ ...pagination, itemsPerPage: items, currentPage: 1 });

  const isApiKeyData = isFetched && !isLoading && data.length === 0;

  return (
    <div className={styles.apiKeysTable}>
      <div className={styles.apiKeysTableHeader}>
        {isApiKeyData ? <div /> : <SearchBar onChange={() => {}} />}
        <div className={styles.apiKeysTableButtons}>
          {/* The docs are Alli AI branded, so they stay off white-label hosts. */}
          {!isWhiteLabel() && (
            <a
              href={API_DOCS_URL}
              className={styles.apiKeysTableDocsLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              View API Documentation
            </a>
          )}
          <Button
            withSpinner={isPendingApiKeyCreate}
            className={styles.apiKeysTableButton}
            onClick={() => onCreate()}
          >
            <Icon iconName="PlusIcon" />
            Generate Key
          </Button>
        </div>
      </div>
      <>
        {isApiKeyData ? (
          <NoData title="No API keys" />
        ) : (
          <>
            <table className={styles.apiKeysTableTable}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {isLoading
                  ? Array.from({ length: 10 }).map((_, index) => (
                      <tr key={index}>
                        {columns.map((_, colIndex) => (
                          <td key={colIndex}>
                            <Skeleton />
                          </td>
                        ))}
                      </tr>
                    ))
                  : table.getRowModel().rows.map((row) => (
                      <tr key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
              </tbody>
            </table>
            <Pagination
              {...pagination}
              disabled={isLoading}
              totalItems={pagination.totalItems}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </>
        )}
      </>
      <Modal
        key="contactDeveloper"
        isOpen={openModal}
        closeModal={handleClose}
        buttonLabel="Revoke"
        formId="contactDeveloper"
        action={handleDelete}
        modalLabel={
          <h3 className={styles.modalTitle}>
            {/*
             // @ts-expect-error TS2339 non-existing className */}
            <div className={styles.dangerIconWrapper}>
              <Icon iconName="DangerIcon" width="1rem" height="1rem" />
            </div>
            Revoke Key
          </h3>
        }
      >
        <div className={styles.modalText}>
          You are about to revoke the API key. This key will no longer be
          available, and systems connected to this key will be disconnected. Are
          you sure you want to revoke the API key?
        </div>
      </Modal>
    </div>
  );
};

export default ApiKeysTable;
